import type { UIMessage } from 'ai';
import type { SearchActivitiesFilters } from '@funkey/ai-tools/schemas';
import { buildBridgeWrite, pickLatestSearchToolPart } from './client/ActivityAiResultsBridge';

/**
 * `ActivityAi_ModeCarryover` — AI → Filter, the toggle-time half (FUN-4891).
 * Pure mapper from the AI's last applied `search_activities` filters onto the
 * filter-drawer's URL params, fired once when the user switches OUT of AI
 * mode (`ActivitySearchModeBridge`'s `setMode`) — no ambient per-turn write.
 * Reuses `buildBridgeWrite`'s field mapping: semantic-only fields
 * (`expandedQuery`, themes/vibe) never entered that mapping, so they drop for
 * free. `page`/`resultMessageId` are AI-mode-only echo fields with no
 * filter-drawer equivalent and are dropped here.
 *
 * The reverse direction (Filter → AI) needs no mapper of its own: the
 * filter-drawer's live values are already snapshotted onto `activeFilters` at
 * send time (`ActivityAiChatPanel`'s `dispatchPrompt`).
 */
export function mapAiFiltersToFilterState(filters: SearchActivitiesFilters) {
    const { page, resultMessageId, ...filterState } = buildBridgeWrite(filters, []).urlPayload;
    return filterState;
}

/**
 * The filters to carry over on the `ai` → `normal` toggle, or `null` when
 * there's nothing safe to carry. Only a RESOLVED (`output-available`) turn
 * qualifies — the mode toggle has no loading state to wait on, so if the user
 * switches while a `search_activities` call is still in flight, `filters`
 * would come from the model's raw tool-call input, which never carries the
 * server-resolved `categoryIds`/`subCategoryIds` (those only exist on the
 * tool's output). Carrying that partial snapshot would silently drop the
 * category filter instead of applying it.
 */
export function pickCarryoverFilters(
    messages: ReadonlyArray<UIMessage>,
): SearchActivitiesFilters | null {
    const search = pickLatestSearchToolPart(messages)?.search;
    if (!search || search.state !== 'output-available') return null;
    return search.filters;
}
